import { cn } from '@/lib/utils'
import { Typo3SectionSpotHeadlessFeature_Item } from '@/types/typo3'
import { BaseLink, Chip, Icon, Img, Title } from '@/ui/components'
import { memo, useCallback, useState } from 'react'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data: Typo3SectionSpotHeadlessFeature_Item
  length: number
  index: number
  className?: string
  onLinkFocus: (index: number) => void
  cardWrapperClassName?: string
  cardWrapperStyle?: React.CSSProperties
  props?: React.HTMLAttributes<HTMLDivElement>
}

const Card = ({
  data,
  length,
  index,
  className,
  cardWrapperClassName,
  cardWrapperStyle,
  onLinkFocus,
  ...props
}: Props) => {
  const onLinkFocusHandle = useCallback(
    (index: number) => {
      onLinkFocus(index)
    },
    [onLinkFocus]
  )

  const [isOver, setIsOver] = useState(false)

  const handleMouseEnter = useCallback(() => {
    setIsOver(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsOver(false)
  }, [])

  return (
    <div
      className={cn(
        'absolute inset-0 flex h-screen flex-col items-center justify-center',
        cardWrapperClassName
      )}
      style={cardWrapperStyle}
      data-stack-card-wrapper
    >
      <div className={'w-full'} {...props}>
        <BaseLink
          href={data.link?.href}
          title={data.link?.title || ''}
          onFocus={() => {
            onLinkFocusHandle(index)
          }}
          className={cn(
            'bg-accent-100 relative z-(--card-zindex) flex w-full flex-col border-b-4 border-white md:grid md:aspect-2/1 md:grid-cols-16',
            className
          )}
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={() => handleMouseLeave()}
        >
          <div className="relative overflow-hidden max-md:aspect-video md:absolute md:inset-0 md:left-1/2">
            {data?.images?.length && (
              <Img
                data={data?.images[0]}
                objectCover={true}
                naked={true}
                className={cn(
                  'image-anim h-full w-full',
                  isOver && 'image-anim-hover'
                )}
                quality={80}
                crop={{
                  width: 645,
                  height: 645
                }}
              />
            )}
          </div>
          <div className="z-10 col-span-9 flex h-full flex-col justify-between">
            <div className="p-md text-accent-300 flex items-start max-md:ml-auto">
              <span className="display">0{index + 1}</span>
              <span className="textstyle-lg-regular">/ 0{length}</span>
            </div>
            <div>
              <Chip>{data.subtitle}</Chip>
              <span className="bg-accent-400 grid w-full grid-cols-[auto_1.5rem] text-white">
                <div className="p-md">
                  <Title as="h3" className="h1 max-md:line-clamp-3">
                    {data.title}
                  </Title>
                </div>
                <div className="bg-accent-100 flex items-center text-neutral-600">
                  <Icon
                    icon={'material-symbols:chevron-right'}
                    className="h-6 w-6"
                  />
                </div>
              </span>
            </div>
          </div>
        </BaseLink>
      </div>
    </div>
  )
}

Card.displayName = 'SliderHomeCard'

export default memo(Card)
